import { Calendar } from '@journ/ui/calendar'
import { cn } from '@journ/ui/cn'
import { Popover, PopoverContent, PopoverTrigger } from '@journ/ui/popover'
import { format, parseISO } from '@/libs/date'
import { useState } from 'react'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'

type Props = {
	value?: string
	onChange: (value: string) => void
	placeholder?: string
	disabled?: boolean
	toDate?: Date
	fromDate?: Date
}

const toDate = (value: string | undefined): Date | undefined => {
	if (!value) return undefined
	try {
		return parseISO(value)
	} catch {
		return undefined
	}
}

const DatePicker = ({ value, onChange, placeholder = 'Select date', disabled, toDate: maxDate, fromDate: minDate }: Props) => {
	const [open, setOpen] = useState(false)
	const { formatPreferenceDate } = useDateFormat()
	const selected = toDate(value)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type='button'
					disabled={disabled}
					onClick={() => setOpen(true)}
					className={cn(
						'h-9 w-full px-3 border border-border bg-transparent text-sm text-left disabled:opacity-50',
						!selected ? 'text-muted-foreground' : undefined,
					)}
				>
					{selected ? formatPreferenceDate(selected) : placeholder}
				</button>
			</PopoverTrigger>
			<PopoverContent className='w-auto p-0' align='end'>
				<Calendar
					mode='single'
					selected={selected}
					onSelect={date => {
						if (date) {
							onChange(format(date, 'yyyy-MM-dd'))
							setOpen(false)
						}
					}}
					disabled={date =>
						(maxDate ? date > maxDate : false) || (minDate ? date < minDate : false)
					}
					initialFocus
				/>
			</PopoverContent>
		</Popover>
	)
}

export { DatePicker }
